import { Component, type ErrorInfo, type ReactNode } from 'react';

type ErrorBoundaryProps = {
	children: ReactNode;
	/** Rendered in place of `children` once a descendant throws. */
	fallback: ReactNode;
	/** Called after an error is caught - use it to report to logging/analytics. */
	onError?: (error: Error, info: ErrorInfo) => void;
};

type ErrorBoundaryState = { hasError: boolean };

/**
 * Catches render / lifecycle errors thrown anywhere in its subtree so a single unexpected failure in
 * the booking widget can't unmount the whole PDP / Signature PDP tree (`PdpTemplate`) along with it -
 * previously any throw here (e.g. the sparse `details.fields` bug in `build-booking-form-state.ts`)
 * blanked the entire product page. Once an error is caught, `fallback` renders instead of the subtree;
 * the error is still logged (and handed to `onError`) so it stays visible for debugging.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): ErrorBoundaryState {
		return { hasError: true };
	}

	override componentDidCatch(error: Error, info: ErrorInfo) {
		// eslint-disable-next-line no-console
		console.error('[ProductBookingWidget] caught an unexpected error, rendering fallback:', error);
		this.props.onError?.(error, info);
	}

	override render() {
		return this.state.hasError ? this.props.fallback : this.props.children;
	}
}
